/**
 * A hook that manages the visibility state for the Elevio `Lightbox` component.
 *
 * To activate the `Lightbox`, pass the `onLightboxElementClick` trigger to a HTML element. The `image` value and `hide` callbacks need to be placed on the `Lightbox` component.
 *
 * > **💡 NOTE**: `onLightboxELementClick` will only work when the component contains an `img` element.
 *
 * @module
 */
import { useState } from 'react';
/**
 * # useLightbox
 *
 * Usage example:
 *
 * ```javascript
 * import useLightbox from '@elevio/kb-kit/lib/hooks';
 *
 * const MyLightBox = () => {
 *   const { image, hide, onLightboxElementClick } = useLightbox();
 *   console.log(image); // HTMLImageElement | undefined
 *   console.log(hide); // () => void
 *   console.log(onLightboxElementClick); // (e: React.MouseEvent) => void
 *
 *   return (
 *     <div>
 *       <Lightbox image={image} isShowing={!!image} hide={hide} />
 *       <ArticleContent.Body onClick={onLightboxElementClick} />
 *     </div>
 *   );
 * };
 * ```
 */
export function useLightbox() {
    const [image, setImage] = useState(undefined);
    const onLightboxElementClick = (e) => {
        const target = e.target;
        if (target.nodeName === 'IMG') {
            const imageElement = target;
            if (imageElement.src.length) {
                e.preventDefault();
                e.stopPropagation();
                setImage(imageElement);
            }
        }
    };
    const hide = () => setImage(undefined);
    return { image, onLightboxElementClick, hide };
}
//# sourceMappingURL=useLightbox.js.map